import React from "react";
import { EmptyState } from "../EmptyState";
import { ClockIcon } from "../../icons";

/**
 * Standard audit log entry interface.
 * Matches the unified backend DTO format.
 */
export interface AuditLogEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  userId: string | null;
  userType: "admin" | "system" | "api";
  userName: string | null;
  changes: Record<string, { old: unknown; new: unknown }> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * Action type labels (Polish)
 */
const ACTION_LABELS: Record<string, string> = {
  create: "Utworzono",
  created: "Utworzono",
  update: "Zaktualizowano",
  updated: "Zaktualizowano",
  delete: "Usunięto",
  deleted: "Usunięto",
  approve: "Zatwierdzono",
  reject: "Odrzucono",
  duplicated: "Zduplikowano",
  set_as_default: "Ustawiono jako domyślne",
};

/**
 * User type labels (Polish)
 */
const USER_TYPE_LABELS: Record<string, string> = {
  admin: "Admin",
  system: "System",
  api: "API",
};

/**
 * Format date and time for display
 */
const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Field name labels registry.
 * Extend this for entity-specific labels.
 */
export const DEFAULT_FIELD_LABELS: Record<string, string> = {
  // Common fields
  name: "Nazwa",
  description: "Opis",
  status: "Status",
  isDefault: "Domyślna",
  // Grant call fields
  title: "Tytuł",
  callNumber: "Numer naboru",
  applicationStartDate: "Data rozpoczęcia",
  applicationEndDate: "Data zakończenia",
  allocation: "Alokacja",
  targetRegions: "Regiony",
  fundingRange: "Zakres finansowania",
  officialUrl: "Oficjalny URL",
  applicationUrl: "URL aplikacji",
  targetGroup: "Grupa docelowa",
  announcedAt: "Data ogłoszenia",
  // ProcessingConfig fields
  contentExtraction: "Ekstrakcja treści",
  classification: "Klasyfikacja",
  dataExtraction: "Ekstrakcja danych",
  config: "Konfiguracja",
};

/**
 * Escape HTML entities to prevent XSS attacks.
 * Only escapes string values, not objects/arrays.
 */
const escapeHtml = (str: string): string => {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

interface FormatValueOptions {
  /** Custom formatters for specific fields */
  fieldFormatters?: Record<string, (value: unknown) => string>;
}

/**
 * Format a single value for diff display
 */
export const formatValue = (
  value: unknown,
  options?: FormatValueOptions
): string => {
  if (value === null || value === undefined) return "(brak)";
  if (typeof value === "boolean") return value ? "Tak" : "Nie";
  if (typeof value === "number") {
    // Check if it's a currency value (large numbers)
    if (value > 1000) {
      return new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
        currencyDisplay: "code",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return value.toString();
  }
  if (typeof value === "string") {
    // Try to parse as date
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return new Date(value).toLocaleDateString("pl-PL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch {
        return escapeHtml(value);
      }
    }
    return escapeHtml(value);
  }
  if (Array.isArray(value)) {
    return value.join(", ") || "(pusta lista)";
  }
  // For objects, show a summary or JSON
  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length <= 3) {
      return JSON.stringify(value, null, 0);
    }
    return `{${keys.length} pól}`;
  }
  return JSON.stringify(value);
};

interface ChangeDiffProps {
  changes: Record<string, { old: unknown; new: unknown }>;
  fieldLabels?: Record<string, string>;
  fieldFormatters?: Record<string, (value: unknown) => string>;
}

/**
 * Component for displaying change diff with old/new values
 */
export const ChangeDiff: React.FC<ChangeDiffProps> = ({
  changes,
  fieldLabels = DEFAULT_FIELD_LABELS,
  fieldFormatters,
}) => {
  const entries = Object.entries(changes);

  if (entries.length === 0) {
    return (
      <span className="text-neutral-500 text-sm">Brak szczegółów zmian</span>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      {entries.map(([field, { old: oldValue, new: newValue }]) => {
        const label = fieldLabels[field] || field;
        const formatter = fieldFormatters?.[field];
        const oldFormatted = formatter
          ? formatter(oldValue)
          : formatValue(oldValue);
        const newFormatted = formatter
          ? formatter(newValue)
          : formatValue(newValue);

        return (
          <div key={field} className="text-sm">
            <span className="font-medium text-neutral-700">{label}:</span>
            <div className="ml-4 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span className="text-red-600 line-through">{oldFormatted}</span>
              <span className="hidden sm:inline text-neutral-400">→</span>
              <span className="text-green-600">{newFormatted}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface AuditLogEntryRowProps {
  entry: AuditLogEntry;
  fieldLabels?: Record<string, string>;
  fieldFormatters?: Record<string, (value: unknown) => string>;
}

/**
 * Single audit log entry row
 */
export const AuditLogEntryRow: React.FC<AuditLogEntryRowProps> = ({
  entry,
  fieldLabels,
  fieldFormatters,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;

  const actionLabel = ACTION_LABELS[entry.action] || entry.action;
  const userTypeLabel = USER_TYPE_LABELS[entry.userType] || entry.userType;

  return (
    <div className="py-3 border-b border-neutral-100 last:border-b-0">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Timeline dot */}
          <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-1.5" />

          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-neutral-900">{actionLabel}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                {userTypeLabel}
              </span>
            </div>

            {entry.userName && (
              <div className="text-sm text-neutral-600 mt-0.5">
                {entry.userName}
              </div>
            )}

            {/* Expandable changes section */}
            {hasChanges && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-sm text-primary-600 hover:text-primary-800 mt-1"
              >
                {isExpanded ? "Ukryj zmiany ▲" : "Pokaż zmiany ▼"}
              </button>
            )}

            {isExpanded && entry.changes && (
              <ChangeDiff
                changes={entry.changes}
                fieldLabels={fieldLabels}
                fieldFormatters={fieldFormatters}
              />
            )}
          </div>
        </div>

        <span className="text-sm text-neutral-500 whitespace-nowrap">
          {formatDateTime(entry.createdAt)}
        </span>
      </div>
    </div>
  );
};

interface AuditLogTimelineProps {
  entries: AuditLogEntry[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  emptyDescription?: string;
  fieldLabels?: Record<string, string>;
  fieldFormatters?: Record<string, (value: unknown) => string>;
  /** Show "Load more" button if there are more entries */
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

/**
 * Generic audit log timeline component.
 *
 * Displays a timeline of audit log entries with expandable diffs.
 *
 * @see docs/specifications/2026-01-18-unified-audit-logging.md
 */
export const AuditLogTimeline: React.FC<AuditLogTimelineProps> = ({
  entries,
  isLoading,
  error,
  onRetry,
  emptyMessage = "Brak historii zmian",
  emptyDescription = "Żadne zmiany nie zostały jeszcze zarejestrowane.",
  fieldLabels = DEFAULT_FIELD_LABELS,
  fieldFormatters,
  hasMore,
  onLoadMore,
  loadingMore,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div
        className="animate-pulse space-y-3"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="sr-only">Ładowanie historii zmian...</span>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-neutral-200" />
            <div className="flex-1">
              <div className="h-4 bg-neutral-200 rounded w-1/3 mb-1" />
              <div className="h-3 bg-neutral-100 rounded w-1/4" />
            </div>
            <div className="h-4 bg-neutral-200 rounded w-24" />
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-amber-600 mb-2">⚠️</div>
        <p className="text-neutral-600 mb-3">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-primary-600 hover:text-primary-800 font-medium"
          >
            Spróbuj ponownie
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <EmptyState
        icon={<ClockIcon className="h-8 w-8 mx-auto" />}
        title={emptyMessage}
        description={emptyDescription}
        className="py-8"
      />
    );
  }

  // Data state
  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-[3px] top-4 bottom-4 w-0.5 bg-neutral-200" />

      {entries.map((entry) => (
        <AuditLogEntryRow
          key={entry.id}
          entry={entry}
          fieldLabels={fieldLabels}
          fieldFormatters={fieldFormatters}
        />
      ))}

      {/* Load more button */}
      {hasMore && onLoadMore && (
        <div className="mt-4 text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="text-sm text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50"
          >
            {loadingMore ? "Ładowanie..." : "Załaduj więcej"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogTimeline;
